/*
 * JLMBOX custom scripts — every behaviour we add on top of the theme lives here.
 */

/*
 * Sticky add-to-cart: while the bar is showing, lift the floating buttons that sit at the
 * bottom of the screen (Shopify Inbox chat, Smile rewards, back-to-top) above it.
 * Sets body.jlm-sticky-atc-visible and --jlm-sticky-atc-height; the CSS does the moving.
 */
(() => {
  const update = bar => {
    const visible = bar.classList.contains('visible');
    document.body.classList.toggle('jlm-sticky-atc-visible', visible);
    if ( visible ) document.documentElement.style.setProperty('--jlm-sticky-atc-height', `${bar.offsetHeight}px`);
  };

  const watch = bar => {
    new MutationObserver(() => update(bar)).observe(bar, { attributes: true, attributeFilter: ['class'] });
    new ResizeObserver(() => update(bar)).observe(bar);
    update(bar);
  };

  const start = () => {
    const bar = document.querySelector('sticky-add-to-cart');
    if ( bar ) watch(bar);
  };

  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', start) : start();
})();

/*
 * Cart drawer: "Versand" (shipping costs) opens in a popup window over the cart instead of
 * leaving the cart. The page content comes from its popup view (templates/page.popup.liquid).
 * Shopify forbids iframes for store pages, so the content is inserted here; its element ids are
 * renamed (jlm-… → jlmpop-…) so they don't clash with the same page shown in the product page
 * "Versandkosten" tab, and its script is run again. On any error the link opens the page.
 */
(() => {
  let dialog;

  const build = () => {
    dialog = document.createElement('dialog');
    dialog.className = 'jlm-popup';
    dialog.innerHTML = `
      <div class="jlm-popup__header">
        <span class="jlm-popup__title"></span>
        <button type="button" class="jlm-popup__close" aria-label="${document.documentElement.lang.startsWith('de') ? 'Schließen' : 'Close'}">
          <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
        </button>
      </div>
      <div class="jlm-popup__body"></div>`;
    document.body.appendChild(dialog);
    dialog.querySelector('.jlm-popup__close').addEventListener('click', () => dialog.close());
    // click on the dark background closes it
    dialog.addEventListener('click', e => { if ( e.target === dialog ) dialog.close(); });
  };

  const load = async href => {
    const url = new URL(href, location.href);
    url.searchParams.set('view', 'popup');
    const res = await fetch(url.href, { credentials: 'same-origin' });
    if ( !res.ok ) throw new Error(res.status);
    const html = (await res.text()).replace(/(["'#])jlm-/g, '$1jlmpop-');
    const tpl = document.createElement('template');
    tpl.innerHTML = html;
    const content = tpl.content.querySelector('.jlm-popup__content');
    if ( !content ) throw new Error('no content');
    const body = dialog.querySelector('.jlm-popup__body');
    body.replaceChildren(content);
    // scripts inserted this way don't run — replace each with a fresh copy so it does
    content.querySelectorAll('script').forEach(old => {
      const s = document.createElement('script');
      s.textContent = old.textContent;
      old.replaceWith(s);
    });
    dialog.querySelector('.jlm-popup__title').textContent = content.dataset.title || '';
    dialog.dataset.href = href;
  };

  document.addEventListener('click', async e => {
    const link = e.target.closest('#site-cart-sidebar .cart__shipping a[href*="/pages/"], #AjaxCartSubtotal .cart__shipping a[href*="/pages/"]');
    if ( !link || typeof HTMLDialogElement === 'undefined' ) return;
    e.preventDefault();
    if ( !dialog ) build();
    try {
      if ( dialog.dataset.href !== link.href ) await load(link.href);
      dialog.showModal();
    } catch (err) {
      location.href = link.href;
    }
  });
})();

/* Back-to-top: show it only once the visitor is far down the page (1.5 screens), not after
   100px like the theme — on a phone it otherwise sits over the products all the time. */
(() => {
  const update = () => document.body.classList.toggle('jlm-scrolled-far', window.scrollY > window.innerHeight * 1.5);
  window.addEventListener('scroll', update, { passive: true });
  update();
})();

/*
 * Free-gift bar (snippets/jlm-cart-gift-progress.liquid) in non-euro carts (CHF, NIS, …).
 * The 89 € threshold is converted with Shopify's own exchange rate (Shopify.currency.rate, the
 * rate the shop uses for its prices) and written in the cart's money format, taken from a sample
 * amount the snippet renders. Runs again whenever the cart drawer re-renders the bar.
 */
(() => {
  const formatter = sample => {
    const m = sample.match(/1(\D?)234(\D)56/);
    if ( !m ) return null;
    const [whole, thousands, decimal] = m;
    return cents => {
      const [int, dec] = (Math.max(cents, 0) / 100).toFixed(2).split('.');
      return sample.replace(whole, int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + decimal + dec);
    };
  };

  const fill = el => {
    const rate = parseFloat(window.Shopify && Shopify.currency && Shopify.currency.rate);
    const format = formatter(el.dataset.money || '');
    if ( !rate || !format ) return;
    const threshold = Math.round(Number(el.dataset.threshold) * rate);
    const total = Number(el.dataset.total);
    const missing = threshold - total;
    const pct = Math.min(100, Math.floor(total * 100 / threshold));
    const text = el.querySelector('.jlm-gift__text span');
    if ( missing > 0 ) {
      text.innerHTML = el.dataset.textMissing.replace('%AMOUNT%', format(missing));
    } else {
      text.textContent = el.dataset.textReached;
      el.classList.add('jlm-gift--reached');
    }
    const bar = el.querySelector('.jlm-gift__bar');
    bar.setAttribute('aria-valuenow', pct);
    bar.querySelector('span').style.width = `${pct}%`;
    el.removeAttribute('data-jlm-gift');
    el.hidden = false;
  };

  const run = () => document.querySelectorAll('[data-jlm-gift]').forEach(fill);
  new MutationObserver(run).observe(document.documentElement, { childList: true, subtree: true });
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', run) : run();
})();

/*
 * November sale tiers in the cart bar (snippets/jlm-cart-gift-progress.liquid) for non-euro carts.
 * The tier amounts (euro) are converted with Shopify.currency.rate, like the free-gift bar above,
 * and written in the cart's money format. Runs again whenever the cart drawer re-renders the bar.
 */
(() => {
  const formatter = sample => {
    const m = sample.match(/1(\D?)234(\D)56/);
    if ( !m ) return null;
    const [whole, thousands, decimal] = m;
    return cents => {
      const [int, dec] = (Math.max(cents, 0) / 100).toFixed(2).split('.');
      return sample.replace(whole, int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + decimal + dec);
    };
  };

  const fill = el => {
    const rate = parseFloat(window.Shopify && Shopify.currency && Shopify.currency.rate);
    const format = formatter(el.dataset.money || '');
    if ( !rate || !format ) return;
    const d = el.dataset;
    const tiers = JSON.parse(d.jlmTiers).map(t => ({ ...t, at: Math.round(t.cents * rate) }));
    const total = Number(d.total);
    const offer = t => {
      const off = d.percent.replace('%NUMBER%', t.percent);
      return t.gift ? d.withGift.replace('%OFFER%', off) : off;
    };
    const reached = tiers.filter(t => total >= t.at).pop();
    const next = tiers.find(t => total < t.at);
    const text = el.querySelector('.jlm-gift__text span');
    // the gift icon only follows a text that names the gift, at its end
    const icon = t => (t && t.gift && d.giftIcon ? ` ${d.giftIcon}` : '');
    if ( !next ) {
      text.innerHTML = d.textTop.replace('%OFFER%', offer(reached)) + icon(reached);
      el.classList.add('jlm-gift--reached');
    } else if ( reached ) {
      text.innerHTML = d.textNext.replace('%CURRENT%', offer(reached)).replace('%AMOUNT%', format(next.at - total)).replace('%OFFER%', offer(next)) + icon(next);
    } else {
      text.innerHTML = d.textMissing.replace('%AMOUNT%', format(next.at - total)).replace('%OFFER%', offer(next)) + icon(next);
    }
    const top = tiers[tiers.length - 1].at;
    const pct = Math.min(100, Math.floor(total * 100 / top));
    const bar = el.querySelector('.jlm-gift__bar');
    bar.setAttribute('aria-valuenow', pct);
    bar.querySelector('span').style.width = `${pct}%`;
    el.removeAttribute('data-jlm-tiers');
    el.hidden = false;
  };

  const run = () => document.querySelectorAll('[data-jlm-tiers]').forEach(fill);
  new MutationObserver(run).observe(document.documentElement, { childList: true, subtree: true });
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', run) : run();
})();

/*
 * Product page: the "Neu" tag sits in the price row, next to "Sie sparen …" (on sale) or next to the
 * price (not on sale). The text comes from Local's hidden badges block (templates/product.json, the
 * same "Neu"/"New" as on the cards; nothing when sold out). The theme rewrites the price on every
 * variant change, so the tag is placed again whenever the product page changes.
 * Styles: "product page: Neu sits in the price row" in assets/jlmbox-custom.css.
 */
(() => {
  const place = page => {
    const block = page.querySelector('.product__badges');
    const price = page.querySelector('.product-text [data-update-block="price-extended"] .product-price');
    if ( !block || !price ) return;
    const src = block.querySelector('.product-item__badge--sold') ? null :
      block.querySelector('.product-item__badge:not(.product-item__badge--sale):not(.product-item__badge--sold):not([class*="preorder"])');
    const text = src ? src.textContent.trim() : '';
    const old = price.querySelector('.jlm-price-tag');
    if ( old && old.textContent === text ) return;
    if ( old ) old.remove();
    if ( !text ) return;
    const tag = document.createElement('span');
    tag.className = 'jlm-price-tag';
    tag.textContent = text;
    (price.querySelector('.product-price--compare') || price).appendChild(tag);
  };
  const run = () => document.querySelectorAll('product-page').forEach(page => {
    place(page);
    if ( !page.jlmPriceTagObserver ) {
      page.jlmPriceTagObserver = new MutationObserver(() => place(page));
      page.jlmPriceTagObserver.observe(page, { childList: true, subtree: true });
    }
  });
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', run) : run();
})();

/*
 * Popups (sections/popups.liquid) — Moran's rules, 2026-10-19:
 *   Offer popup ("Image" block, homepage): once per visit, whatever the customer did.
 *   Newsletter popup: once per visit on every page, after 30% scroll (Customize setting); on the homepage 3 s after
 *   the offer popup is closed instead. Never again once the customer has subscribed.
 * A visit ends after VISIT_MINUTES without opening a page (Shopify's own session rule).
 * Local decides on its own when a popup shows (assets/component-modal.js, loaded after this file):
 * for the offer we only clear or fill its memory; the newsletter we show ourselves and keep Local's
 * memory filled so it never schedules it.
 */
(() => {
  if ( window.Shopify && Shopify.designMode ) return;

  const VISIT_MINUTES = 30;
  const NEWSLETTER_DELAY_AFTER_OFFER = 3000;
  const ACTIVE = `${window.KT_PREFIX || ''}active`;

  const read = key => { try { return JSON.parse(localStorage.getItem(key)); } catch (error) { return null; } };
  const save = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)); } catch (error) { /* blocked */ } };
  const localKey = modal => `modal-${document.location.hostname}-${modal.id}`;
  const localSeen = modal => save(localKey(modal), { shown: Date.now(), content: modal.querySelector('[data-content]').textContent });
  const isActive = modal => modal.classList.contains(ACTIVE);

  // The visit: a new one starts after VISIT_MINUTES without a page view.
  const now = Date.now();
  let visit = read('jlm-visit');
  if ( !visit || now - visit.last > VISIT_MINUTES * 60000 ) visit = { id: now };
  visit.last = now;
  save('jlm-visit', visit);
  const seenThisVisit = key => read(key) === visit.id;
  const markSeen = key => save(key, visit.id);

  // A sign-up from any newsletter form returns with ?customer_posted=true.
  if ( new URLSearchParams(location.search).get('customer_posted') === 'true' ) save('jlm-newsletter-subscribed', true);

  // Offer popup: seen this visit → Local skips it; otherwise Local shows it as configured.
  const offer = document.querySelector('modal-box.popup--image');
  const offerDue = offer && !seenThisVisit('jlm-offer-seen');
  if ( offer ) {
    if ( offerDue ) {
      try { localStorage.removeItem(localKey(offer)); } catch (error) { /* blocked */ }
    } else {
      localSeen(offer);
    }
    new MutationObserver(() => { if ( isActive(offer) ) markSeen('jlm-offer-seen'); })
      .observe(offer, { attributes: true, attributeFilter: ['class'] });
  }

  // Newsletter popup: Local never schedules it; we do.
  const newsletter = document.querySelector('modal-box.popup--newsletter');
  if ( !newsletter ) return;
  localSeen(newsletter);
  if ( read('jlm-newsletter-subscribed') || seenThisVisit('jlm-newsletter-seen') ) return;

  // Scroll depth and delay stay editable in Customize (the newsletter block's own settings).
  let options = {};
  try { options = JSON.parse(newsletter.dataset.options); } catch (error) { /* defaults below */ }
  const scrollShare = options.showOnPageOffset > 0 ? options.showOnPageOffset / 100 : 0.3;
  const scrollDelay = (Number(options.show) || 1) * 1000;

  let done = false;
  const showNewsletter = delay => {
    if ( done ) return;
    done = true;
    setTimeout(() => customElements.whenDefined('modal-box').then(() => {
      if ( offer && isActive(offer) ) { done = false; return; } // never on top of the offer
      newsletter.show();
      markSeen('jlm-newsletter-seen');
    }), delay);
  };

  if ( offerDue ) {
    // Homepage with the offer still to come: the newsletter follows once the offer is closed.
    let offerWasOpen = false;
    new MutationObserver(() => {
      if ( isActive(offer) ) offerWasOpen = true;
      else if ( offerWasOpen ) showNewsletter(NEWSLETTER_DELAY_AFTER_OFFER);
    }).observe(offer, { attributes: true, attributeFilter: ['class'] });
    return;
  }

  const onScroll = () => {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    if ( scrollable > 0 && window.scrollY / scrollable < scrollShare ) return;
    window.removeEventListener('scroll', onScroll);
    showNewsletter(scrollDelay);
  };
  window.addEventListener('scroll', onScroll, { passive: true });
})();
